// Per-project cache for the getProjectAccessInfo callable (1 Oct 2026).
//
// The Collaborator Access dialog used to call it on open and wait. A cold start
// took 6.4s on staging (warm calls 0.3–0.5s), so the dialog sat for seconds.
// Now the collaborators panel PREFETCHES it when it opens, and the dialog reads
// the cached answer; if the prefetch is still in flight it waits for that same
// request (never worse than before). A failed fetch is retried once.
//
// Pure: the fetcher is injected, so this has no Firebase import and is unit-tested.
// eslint-disable-next-line import/prefer-default-export
export function makeAccessInfoCache(fetcher) {
    const entries = new Map(); // pid -> { promise, value?, failed? }

    function prefetch(pid) {
        if (!pid) return Promise.resolve(null);
        const existing = entries.get(pid);
        if (existing && !existing.failed) return existing.promise;
        const entry = {};
        entry.promise = Promise.resolve()
            .then(() => fetcher(pid))
            .then((value) => {
                entry.value = value;
                return value;
            }, (err) => {
                entry.failed = true;
                throw err;
            });
        // A prefetch nobody awaits must not surface as an unhandled rejection.
        entry.promise.catch(() => {});
        entries.set(pid, entry);
        return entry.promise;
    }

    // The answer for a project: cached, in flight, or fetched now; one retry on failure.
    async function get(pid) {
        try {
            return await prefetch(pid);
        } catch (err) {
            entries.delete(pid);
            return prefetch(pid);
        }
    }

    // Drop a project's answer (after an access change), or everything.
    function invalidate(pid) {
        if (pid) entries.delete(pid);
        else entries.clear();
    }

    // The settled answer if there is one, without fetching.
    function peek(pid) {
        const e = entries.get(pid);
        return e && 'value' in e ? e.value : undefined;
    }

    return { prefetch, get, invalidate, peek };
}


// WEBPACK FOOTER //
// ./src/lib/access-info-cache.js